import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@shared/ui/dialog";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { Manufacturer } from "../../types";
import {
  useCreateManufacturer,
  useGetAssetType,
  useUpdateManufacturer,
} from "../../hooks";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { getCountries } from "countrycitystatejson";
import { PageHeader } from "@shared/components/PageHeader";

interface ManufacturerDialogProps {
  isOpen: boolean;
  onClose: () => void;
  editData?: Manufacturer | null;
}

export const ManufacturerDialog: React.FC<ManufacturerDialogProps> = ({
  isOpen,
  onClose,
  editData = null,
}) => {
  const remoteUtilityId = getRemoteUtilityId();
  const countryData = getCountries();
  const { data: assetTypeData } = useGetAssetType();
  const createManufacturer = useCreateManufacturer();
  const updateManufacturer = useUpdateManufacturer();
  const isEditMode = Boolean(editData);

  // Form fields definition
  const formFields: FormField[] = [
    {
      name: "name",
      type: "text",
      label: "Manufacturer Name",
      group: "Basic Manufacturer Information",
      disabled: isEditMode,
      placeholder: "e.g., FlowControl Inc., AquaPump Solutions",
      required: true,
      groupColorNeeded: false,
      className: "col-span-2 mt-3",
    },
    {
      name: "primaryContact",
      type: "text",
      label: "Primary Contact Person",
      group: "Contact Information",
      placeholder: "Contact person name",
      required: false,
      groupColorNeeded: false,
      className: "col-span-1 mt-3",
    },
    {
      name: "contactEmail",
      type: "email",
      label: "Contact Email",
      group: "Contact Information",
      placeholder: "contact@company.com",
      required: true,
      className: "col-span-1 mt-3",
    },
    {
      name: "phoneNumber",
      type: "text",
      label: "Phone Number",
      group: "Contact Information",
      placeholder: "Phone number",
      required: false,
      className: "col-span-1 mt-3",
    },
    {
      name: "website",
      type: "text",
      label: "Website",
      group: "Contact Information",
      placeholder: "www.example.com",
      required: false,
      className: "col-span-1 mt-3",
    },
    {
      name: "country",
      type: "multi-select",
      maxSelection: 1,
      size: "medium",
      variant: "outlined",
      label: "Country",
      group: "Contact Information",
      placeholder: "Select country",
      required: false,
      options: (countryData || []).map((country) => ({
        value: country.name,
        label: country.name,
      })),
      className: "col-span-2 mt-3",
    },
    {
      name: "productCategories",
      type: "multi-select",
      label: "Asset Types",
      group: "Asset Types",
      placeholder: "Select asset types",
      required: false,
      groupColorNeeded: false,
      options: (assetTypeData || []).map((assetType) => ({
        value: assetType.name,
        label: assetType.name,
      })),
      helperText: "Select the types of assets this manufacturer produces",
      className: "col-span-2 mt-3",
    },
    {
      name: "notes",
      type: "textarea",
      label: "Notes",
      group: "Asset Types",
      placeholder: "Any additional relevant information about the manufacturer",
      rows: 3,
      required: false,
      className: "col-span-2 mt-3",
    },
  ];

  // Create form with proper default values
  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false, // Changed to false to prevent validation before user interaction
    defaultValues: {
      name: "",
      primaryContact: "",
      contactEmail: "",
      phoneNumber: "",
      website: "",
      country: "",
      status: "Active",
      productCategories: [],
      notes: "",
    },
  });

  // Populate form when edit data changes or dialog opens
  useEffect(() => {
    if (isOpen) {
      const initialValues = {
        name: editData?.name || "",
        primaryContact: editData?.manufacturerContactPerson || "",
        contactEmail: editData?.manufacturerContactEmail || "",
        phoneNumber: editData?.contactNumber || "",
        website: editData?.website || "",
        country: [editData?.country || ""],
        status: editData?.isActive || "Active",
        productCategories: editData?.category || [],
        notes: editData?.description || "",
      };

      // Use FormService.populateForm for proper form population
      FormService.populateForm(form, initialValues, formFields);
    }
  }, [isOpen, editData]);

  // Handle form submission
  const handleSubmit = async (formValues: any) => {
    try {
      const operation = isEditMode ? "update" : "create";

      if (operation === "update") {
        const payload = {
          id: editData?.id,
          remote_utility_id: remoteUtilityId,
          manufacturer_contact_email: formValues.contactEmail,
          manufacturer_contact_person: formValues.primaryContact,
          description: formValues.notes,
          website: formValues.website,
          country: formValues.country[0],
          contact_number: formValues.phoneNumber,
          category: formValues.productCategories,
        };
        await updateManufacturer.mutateAsync(payload);
      } else {
        const payload = {
          name: formValues.name,
          remote_utility_id: remoteUtilityId,
          manufacturer_contact_email: formValues.contactEmail,
          manufacturer_contact_person: formValues.primaryContact,
          description: formValues.notes,
          website: formValues.website,
          country: formValues.country[0],
          contact_number: formValues.phoneNumber,
          category: formValues.productCategories,
        };
        await createManufacturer.mutateAsync(payload);
      }

      handleClose();
    } catch (error) {
      console.error("Form submission error:", error);
      // Handle error appropriately - maybe show a toast or error message
    }
  };

  const handleClose = () => {
    form.reset({
      name: "",
      primaryContact: "",
      contactEmail: "",
      phoneNumber: "",
      website: "",
      country: "",
      status: "Active",
      productCategories: [],
      notes: "",
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <PageHeader
            title={isEditMode ? "Edit Manufacturer" : "Add New Manufacturer"}
            subtitle="Configure manufacturer details, contact information, and product categories"
          />
        </DialogHeader>

        <div className="space-y-6">
          <DynamicForm
            fields={formFields}
            form={form}
            onSubmit={handleSubmit}
            onReset={handleClose}
            gridColumns={2}
            responsiveColumns={{
              sm: 1,
              md: 2,
            }}
            config={{
              showResetButton: true,
              showSubmitButton: true,
              submitButtonText: isEditMode
                ? "Update Manufacturer"
                : "Add Manufacturer",
              resetButtonText: "Cancel",
              spacing: "comfortable",
              classes: {
                form: "space-y-6 p-0",
                button: "px-4 py-2 rounded-md font-medium transition-colors",
                buttonContainer:
                  "flex justify-end gap-3 pt-6 border-gray-200 mt-8",
                input:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
                textarea:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical",
                select:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white",
                label: "block text-sm font-medium text-gray-700 mb-1",
                error: "text-sm text-red-600 mt-1",
                helperText: "text-sm text-gray-500 mt-1",
                container: "space-y-1",
              },
            }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
